import { FunctionComponent, Fragment, useContext, useCallback } from 'react'
import contextLayoutMultiLine from '@contexts/layoutMultiLine'
import contextMegaModule from '@contexts/megaModule'
import { TOITeaserAPIProps } from './types'
import TOITeaserLayoutContextProvider from './TOITeaserLayoutContextProvider'
import DefaultTOILayout from './layouts/Default'
import OpinionTOILayout from './layouts/Opinion'
import { CookWidget } from '@widgets/types'
import useViewportTracking from '@hooks/useViewportTracking'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { stripHtml } from '@hooks/useTracking/utils'
import BlickTVTeaser from '@widgets/BlickTVTeaser'
import useAureusImpressionTracking from '@hooks/useAureusImpressionTracking'

type ToiTeaserProps = TOITeaserAPIProps

const { MegaModuleContext } = contextMegaModule
const { LayoutMultiLineContext } = contextLayoutMultiLine

const isValidTextPositionV = (
  textPosition: TOITeaserAPIProps['textPosition']
) => textPosition.v === 'top' || textPosition.v === 'bottom'

const isValidTextPositionH = (
  textPosition: TOITeaserAPIProps['textPosition']
) => textPosition.h === 'left' || textPosition.h === 'right'

const ToiTeaser: FunctionComponent<ToiTeaserProps> = (props) => {
  const {
    articleId,
    title,
    subtitle,
    catchword = '',
    label,
    articleType = 'default',
    accentColor,
    bulletpoints,
    logo,
    scribble,
    textPosition,
    matchId,
    typeOfSport,
    video,
    aureusOfferId,
    track,
    smart,
    relatedMatches,
    swisslosUrl,
    link,
    promo,
    parentProps,
    image = {
      src: '/incoming/31574-v2-missing.png',
      alt: 'image not found',
    },
  } = props

  const onImpression = useCallback<TrackingFnType>(() => {
    return {
      event: 'teaser_impression',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:mega:${stripHtml(title)}`,
      eventAction: 'impression',
    }
  }, [articleId, title, video])

  const trackedOnImpression = useTracking(onImpression)

  const onPromoImpression = useCallback(
    () => ({
      event: 'contentcommerce_impression',
      eventCategory: 'BrandStudio',
      eventValue: 1,
      eventAction: 'Impression / Brandstudio',
      nonInteraction: true,
      impressionUrl: promo?.tradeDoublerUrl ?? '',
    }),
    [promo?.tradeDoublerUrl]
  )

  const trackedOnPromoImpression = useTracking(onPromoImpression)

  const triggerAureusImpression = useAureusImpressionTracking()

  const finalOnImpression = useCallback(() => {
    triggerAureusImpression({
      aureusOfferId,
      smart,
    })
    if (!!track) {
      trackedOnImpression()
      if (promo?.isPromo) {
        trackedOnPromoImpression()
      }
    }
  }, [
    aureusOfferId,
    promo?.isPromo,
    smart,
    track,
    trackedOnImpression,
    trackedOnPromoImpression,
    triggerAureusImpression,
  ])

  const viewportRef = useViewportTracking({
    onImpression: finalOnImpression,
    track: true,
  })

  const megaModuleContext = useContext(MegaModuleContext)
  const contextLayoutMultiLine = useContext(LayoutMultiLineContext)

  //! Somehow live-result widget doesn't provide an articleId
  //! at the top level...
  const articleIdIncludingLivescore = articleId ?? link?.targetArticleId

  const onClick = useCallback(
    () => ({
      event: 'teaser_click',
      eventCategory: video ? 'video_teaser' : 'article_teaser',
      eventLabel: `${articleId}:mega:${stripHtml(title)}`,
      eventAction: 'click',
      id: articleId,
    }),
    [articleId, title, video]
  )

  const trackedOnClick = useTracking(onClick)

  const clickableProps = {
    aureusOfferId,
    ...(track ? { onClick: trackedOnClick } : {}),
    ...link,
  }

  //* Blick TV is sent as a Mega Teaser from the API,
  //* so we have to detect that it is indeed it and not
  //* a normal Mega Teaser
  if (video?.isBlickTV) {
    return <BlickTVTeaser title={title} video={video} />
  }

  const { src: imageSrc, alt: imageAlt, crops: imageAvailableCrops } = image

  const teaserParentFlex = parentProps?.flex ?? '6'

  const {
    v: textPositionV,
    h: textPositionH,
  }: TOITeaserAPIProps['textPosition'] =
    textPosition &&
    isValidTextPositionV(textPosition) &&
    isValidTextPositionH(textPosition)
      ? textPosition
      : {
          v: 'bottom',
          h: 'left',
        }

  const hasGrid = !!contextLayoutMultiLine?.grid

  return (
    <Fragment key={articleIdIncludingLivescore}>
      <TOITeaserLayoutContextProvider
        articleType={articleType}
        teaserParentFlex={teaserParentFlex}
        hasParentGrid={hasGrid}
        mainTextPositionV={textPositionV}
        mainTextPositionH={textPositionH}
        accentColor={megaModuleContext?.accentColor ?? accentColor}
        isInMegaModule={!!megaModuleContext}>
        {articleType !== 'default' ? (
          <OpinionTOILayout
            {...{
              label,
              title,
              catchword,
              subtitle,
              imageSrc,
              imageAlt,
              imageAvailableCrops,
              clickableProps,
              video,
              viewportRef,
            }}
          />
        ) : (
          <DefaultTOILayout
            {...{
              label,
              title,
              catchword,
              subtitle,
              promo,
              bulletpoints,
              scribble,
              matchId,
              typeOfSport,
              relatedMatches,
              swisslosUrl,
              imageSrc,
              imageAlt,
              imageAvailableCrops,
              clickableProps,
              logo,
              video,
              viewportRef,
            }}
          />
        )}
      </TOITeaserLayoutContextProvider>
    </Fragment>
  )
}

const widget: CookWidget = {
  kind: [
    ['widget', 'teaser', 'mega'],
    ['widget', 'sport', 'live-result'],
  ],
  component: ToiTeaser,
}

export default widget
